/**
 * The settings cogwheel's window: what LunaPin does with a page when you save it, and how
 * to get pages into it from anywhere else.
 *
 * ── Which of the toolkit's three settings affordances this is ─────────────────
 *
 * The toolkit's top bar can carry three right-hand panels, and they are easy to confuse
 * because two of them are called settings-something in the source:
 *
 *  1. **`buildSettingsGearButton`** — always rendered, and despite the name it paints
 *     `ICON_APPEARANCE`, an "Aa" typography mark. It opens the toolkit's *Appearance*
 *     sidebar: fonts, density, corner radius, the pane theme. `AppShellSpec.settingsHost`
 *     only chooses *which theme host* that sidebar writes through; it does not decide
 *     whether the button exists, and it is not a slot an app can put its own surface in.
 *     LunaPin leaves it unset and gets the toolkit's own host, which is why this file has
 *     nothing to do with it.
 *  2. **`appSettingsContent`** — a gear (`ICON_APP_SETTINGS`, a real cog) opening a
 *     right-side sidebar whose body the app supplies.
 *  3. **`onAppSettingsActivate`** — the same gear, in the same place, invoking the app's
 *     own callback instead of opening that sidebar. Its documentation names exactly this
 *     case: an app that already owns a settings surface "should be able to reach it from
 *     that icon rather than … clone the glyph into their own `extraTopbarTrailing` button
 *     (where it would drift from the toolkit's icon and sit on the wrong side of the
 *     divider)".
 *
 * LunaPin takes (3). It is the declarative route, it costs one line in the spec, and the
 * glyph, the position and the hover treatment stay the toolkit's — which is the whole
 * reason not to hand-roll a `TopbarAction` with a gear of our own. `ICON_APP_SETTINGS` is
 * private to the toolkit anyway, so a hand-rolled button would have had to carry a second
 * copy of the path data, free to drift from the one beside it.
 *
 * ── Why a window and not the modal dialog this was ───────────────────────────
 *
 * It was a `showChoiceDialog`, and LPN-9 moved it into the workspace as
 * `WindowContent.Settings` for three reasons that have nothing to do with how it looks:
 *
 *  - **A modal cannot be kept open.** Settings is a surface you consult *while* doing
 *    something — the bookmarklet is instructions for a thing you do in another tab — and
 *    a container you must dismiss to look at anything else is the wrong one for that.
 *  - **The workspace persists windows and forgets dialogs.** A settings window reopens
 *    where it was left, beside the list it explains; a dialog is stateless furniture.
 *  - **The sibling does it this way.** Lunicle's settings are panes in its workspace, and
 *    a modal was the odd one out in this family.
 *
 * The gear stays idempotent across that move: pressing it twice focuses the window that
 * exists rather than opening a second, which is
 * [se.soderbjorn.lunapin.client.viewmodel.WorkspaceBackingViewModel.onSettingsActivated]'s
 * job and lives in `:client` so the phones inherit it.
 *
 * ── Five tabs, in this order ─────────────────────────────────────────────────
 *
 * **The bookmarklet**, **Tags**, **AI**, **Tools**, **Connections**. A strip across the top,
 * one pane showing, exactly as Lunicle's settings pane — see [SettingsView.buildTabs].
 *
 * This was one scrolling column of four stacked sections until it grew enough to be worth
 * navigating. The argument that used to stand here — *a strip is a control for choosing
 * between things, and there is one thing* — was true of the strip the design drew, which
 * had seven names on it and nothing behind five of them. It is not true of four sections
 * that each fill a screen.
 *
 * Two of the design's seven names are still not built, and that half of the old argument
 * survives intact: **Appearance** and **Tabs & windows** belong to the toolkit. Its "Aa"
 * sidebar already owns fonts, density, corner radius and themes, and it owns window layout
 * outright. A second surface for either is a second surface that drifts from the first.
 *
 * The order is what somebody does first, not what is most complex:
 *
 *  - **The bookmarklet** leads because it is the answer to "how do I get things *into*
 *    this?", which is the only question a new collection has. It is also the one thing here
 *    you do once, drag to a bar, and never open this window for again — so it wants to be
 *    the thing in front of you on day one and easy to skip past on day two.
 *  - **Tags** second: how large a vocabulary you want, and the handful you file under most
 *    — the next thing worth setting up and the one that changes two surfaces at once. The
 *    lists were `DEFAULTS FOR NEW SAVES`, a 17rem panel wedged beside the draggable link,
 *    and they stopped belonging there the moment the list window's tag rail started reading
 *    them too — a setting two surfaces obey cannot be filed inside one of them. The ceiling
 *    above them was on the **AI** tab as *New tags per bookmark*, and moved for the same
 *    kind of reason: it is read by the tidy tool as well as by the enrichment worker, so it
 *    belongs beside the vocabulary it acts on rather than inside one of the things acting.
 *  - **AI** third. The most to read and the least to change: the defaults are chosen to be
 *    right, and somebody who never opens this tab gets summaries and tags anyway.
 *  - **Tools** last, and it is the odd one out on purpose. The backfill is not a
 *    preference at all — it is *one press that does something to every bookmark at
 *    once* — so it is filed apart from the three tabs that decide what the next save
 *    does. These were loose panels at the foot of the AI column, and then a section called
 *    *Your collection*; the name went with the tab, because a tab strip is read as a row of
 *    verbs and "your collection" is not one.
 *  - **Connections** last, and further out still. Every tab before it is the operator acting
 *    on their own collection; this one is about *what else may act on it as them* — the
 *    switch for agent access, the URL to paste into one, and what is currently connected. It
 *    is the tab somebody visits twice: once to set an agent up, and once when they want to
 *    cut it off. See LPN-59.
 *
 * The server's refusal line sits **above the strip**, outside every pane. A refusal painted
 * inside a tab is a refusal nobody sees the moment they switch away from it.
 *
 * ── There is no Save button, and that is a decision ──────────────────────────
 *
 * The design draws a footer with **Cancel** and **Save**. Every control here writes when it
 * is changed instead, and the footer is gone rather than left as a Save that does nothing.
 *
 * A settings *window* is not modal. It can be left open beside the list, it survives a
 * reload, and the toolkit's × is its dismissal — so there is no moment at which somebody
 * "submits the form", and a Save on a surface with no such moment is a button whose job is
 * to be forgotten. *Cancel* is worse: it would have to mean "put back whatever was there
 * when this window opened", a baseline nothing else in this app keeps and which would go
 * stale the first time the window was left open for an afternoon.
 *
 * What that costs is stated where it is paid, in `SettingsBackingViewModel`'s header: a
 * screen that saves on change must never drop a change, and the three things that make that
 * true — writes on a scope that outlives the window, writes serialised against each other,
 * and a refusal shown rather than swallowed — are all in the view model, so every platform
 * gets them.
 *
 * ── Every control here is the toolkit's ──────────────────────────────────────
 *
 * A boolean is an **On/Off pair of `dt-settings-choice-btn`s**, and so is every other
 * choice on this surface: summary length, language, the vocabulary ceiling. That is what lunula
 * spells a choice as — its own Appearance sidebar has no checkbox, no dropdown and no
 * slider in it, only rows of choice buttons with the live one accented — and a LunaPin
 * window sitting inside that shell should not disagree about what a setting looks like.
 * Lunicle reached the same conclusion and wrote the same pair; see its `Dom.kt`.
 *
 * What that replaced was four hand-rolled controls, each fine on its own and none of them
 * the toolkit's: a pill track with a sliding knob, a filled segmented control, a native
 * `<select>` and a `<input type="range">`. Between them they had four ideas about what
 * "chosen" looks like, none matching the sidebar one gear-press away.
 *
 * The class names are the toolkit's own rather than a restyled copy, exactly as the sign-in
 * dialog wears `.dt-modal*`: these follow the toolkit's chrome, its corner radius and every
 * theme's accent for free, and they keep following them after a lunula upgrade.
 *
 * The one thing lost is the cap's **slider**, and it is worth naming: dragging 0→10 is one
 * gesture where pressing is one of eleven targets. Eleven small buttons is what lunula
 * already does for font size, which is a longer ladder than this one, so the idiom is
 * proven at this length and the number is legible without a numeral beside it.
 *
 * ── What the canvas draws that is deliberately not here ──────────────────────
 *
 *  - **An API-key field, *Test key* and *Remove key*.** A deployment secret is not an
 *    account preference, and a field that can edit one is a field that can leak it. What is
 *    drawn instead is a read-only row naming the variable the server actually reads,
 *    padlocked and badged `SET ON THE SERVER`.
 *  - **A *Model* field.** It was built, and LPN-17 removed it. Which model the operator's
 *    key is spent on is the same kind of fact as the key itself — it decides what a summary
 *    costs and how good it is, against the operator's quota — so having one of the pair be
 *    a read-only row and the other a text box any signed-in account could edit was the
 *    inconsistency. `AccountSettings` carries the full argument; the effect here is that
 *    **nothing on this surface is free text**, and every control takes a value from a fixed
 *    set.
 *  - **An *Auto-apply proposed tags* switch.** Tags are applied, never proposed, so there is
 *    nothing waiting to be applied. Its safety argument — a generous new-tag allowance is
 *    safe because nothing lands unreviewed — is answered by the vocabulary ceiling itself
 *    and by *Of which new* in the usage panel.
 *  - **A `SAMPLE OUTPUT` block and *Try on a URL…***. Both are a second way to spend the
 *    operator's key on something nobody asked for, and *Test connection* already answers
 *    "is this working".
 *  - **`Accepted 61%`**, which measured approvals of proposals that no longer exist.
 *
 * ── Why the bookmarklet lives here ───────────────────────────────────────────
 *
 * It was going to be its own server-rendered page at `/bookmarklet`, and this is better
 * for a plain reason: nobody types a URL to look for a setting. The cogwheel is where a
 * person goes to ask "how do I get things *into* this?", and a page they have to be told
 * about is a page they will not find.
 *
 * It also means one construction of the bookmarklet rather than two. The link has to be
 * absolute — a `javascript:` URL runs on somebody else's page, where a relative path
 * means their server — and building it here from `window.location.origin` makes it point
 * at whichever LunaPin served this app, with nothing to configure:
 *
 *   · a local run hands out a link to `http://localhost:8080`
 *   · a deployment at `lunapin.dev` hands out a link to `https://lunapin.dev`
 *   · somebody self-hosting hands out a link to their own host
 *
 * All three are correct at once, and somebody running two of them gets two bookmarklets
 * that save to two collections — which is what they asked for by running two.
 *
 * ── `textContent` throughout ─────────────────────────────────────────────────
 *
 * Every string this file paints is either its own or the server's — including the server's
 * refusal sentences and a *model provider's* error message, which is the one class of
 * string nobody here controls at all. None of it is ever assigned as markup; see `Dom.kt`,
 * where that rule is the whole argument for these two helpers existing.
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import se.soderbjorn.lunapin.client.viewmodel.AGENT_ACCESS_EXPLANATION
import se.soderbjorn.lunapin.client.viewmodel.AGENT_ACCESS_LABEL
import se.soderbjorn.lunapin.client.viewmodel.SettingsBackingViewModel
import se.soderbjorn.lunapin.client.viewmodel.SettingsBackingViewModel.State
import se.soderbjorn.lunapin.clientserver.SummaryLanguages
import se.soderbjorn.lunapin.clientserver.SummaryLengths
import se.soderbjorn.lunapin.clientserver.TagVocabularyTargets

/**
 * The environment variable this deployment reads its model provider key from.
 *
 * Printed on the screen, and printed **here** rather than taken from anything the server
 * says, because the server deliberately says nothing at all about the key beyond a boolean
 * — see `AiConfig`. A route that answered the variable's name would be a route that knows
 * about the key, and the name is a constant of this build in any case.
 *
 * The design canvas prints `LUNAPIN_OPENAI_KEY`, which is not the variable the server
 * reads. This is; sending an operator to export the wrong name is a whole afternoon.
 */
private const val KEY_VARIABLE = "LUNAPIN_KEY_OPENAI"

/**
 * The popup's path on whichever LunaPin served this app.
 *
 * Stated once here because two things now point at it — the bookmarklet and the plain
 * bookmark beside it — and two literals would be one rename away from a link that opens
 * somebody's 404 page.
 */
private const val ADD_PATH = "/add"

/**
 * The `javascript:` URL the user drags to their bookmarks bar.
 *
 * Short on purpose — a bookmarklet is stored as a URL and read by whoever is curious —
 * and every part of it earns its place:
 *
 *  - **`encodeURIComponent` on both values.** A URL with a `&` in its query string would
 *    otherwise arrive truncated, which is the classic bookmarklet bug.
 *  - **`document.title`**, read in the visited page's own context. That is the whole
 *    reason a bookmarklet beats a server-side fetch of the URL: it is exact, free, and
 *    works on pages behind a login or rendered in JavaScript, where a server would get a
 *    sign-in wall or an empty shell.
 *  - **A named window** (`lunapin`), so pressing it twice reuses the same popup rather
 *    than stacking them.
 *  - **A fixed size**, because a popup that opens at the browser's default is a
 *    full-height window for a form five fields tall.
 *
 * ── The fallback, and why silence was the bug (LPN-40) ───────────────────────
 *
 * `open()` can be refused, and when it is it does not throw — it returns `null`, and the
 * old `void(open(…))` threw that answer away. A popup blocker does it; so does an embedded
 * browser with no windows to give. The result was a button that did *nothing whatsoever*,
 * which is indistinguishable from a broken bookmarklet and is the shape LPN-40 was
 * reported in.
 *
 * So the URL is built once into `u`, `open` is tried, and if there is no window the current
 * tab is navigated to the same page instead. That costs the user their place — which is the
 * price the popup design exists to avoid, and is why it is a *fallback* rather than the
 * mechanism — but losing your scroll position beats pressing a button that does nothing,
 * and the page it lands on offers a link straight back. See `Bookmarklet.kt`.
 *
 * The `try` is for the same failure wearing its other face: a few embedded browsers throw a
 * `SecurityError` from `open` rather than returning null, and an exception escaping a
 * bookmarklet is silence again.
 *
 * **What it still cannot do.** A document served under `Content-Security-Policy: sandbox`
 * — Lunicle's attachment previews, for one — never evaluates a `javascript:` URL at all,
 * with or without `allow-scripts`, so none of this runs and no bookmarklet on earth can
 * help. That case is answered by the plain bookmark [saveLinkHref] builds, not here.
 *
 * Internal rather than private so `BookmarkletHrefTest` can pin the parts that are
 * invisible when they break — a missing `encodeURIComponent` only fails on URLs with a
 * `&` in them, which is not the one anybody tests by hand.
 *
 * @param origin the server this app was served from, e.g. `https://lunapin.dev`.
 */
internal fun bookmarkletHref(origin: String): String =
    "javascript:void(function(){" +
        "var u='$origin$ADD_PATH?url='+encodeURIComponent(location.href)" +
        "+'&title='+encodeURIComponent(document.title);" +
        "var w;try{w=open(u,'lunapin','width=460,height=520')}catch(e){}" +
        "if(!w)location.href=u;" +
        "}())"

/**
 * The *ordinary* bookmark beside it: LunaPin's save form, with nothing to fill it in.
 *
 * Not a `javascript:` URL, and that is its entire purpose. A page served under
 * `Content-Security-Policy: sandbox` — an attachment preview, a report opened from an issue
 * tracker — evaluates no `javascript:` URL of ours at all, and neither does Chrome's
 * built-in PDF viewer. On those the bookmarklet is not slow or blocked, it
 * simply never runs, and there is nothing a cleverer bookmarklet could do about it.
 *
 * A plain link has no such problem: it is a navigation, and every page permits one. What it
 * cannot do is *know* the address it came from, so the form it opens asks for it — copy the
 * address, press this, paste. See `Bookmarklet.kt`, where `GET /add` with no `url` renders
 * that form.
 *
 * @param origin as for [bookmarkletHref].
 */
internal fun saveLinkHref(origin: String): String = "$origin$ADD_PATH"

/**
 * The five tabs, in the order they are drawn.
 *
 * `entries` order **is** the strip's order, so this declaration is the only place it is
 * stated — see the file header for why it runs bookmarklet-first rather than leading with
 * the tab that has the most in it.
 */
private enum class Tab(val label: String, val paneClass: String) {
    BOOKMARKLET("Bookmarklet", "lp-pane-bookmarklet"),
    TAGS("Tags", "lp-pane-tags"),
    AI("AI", "lp-pane-ai"),

    /**
     * The bulk actions — today, the backfill.
     *
     * Named for what they are rather than what they act on. This was a section called *Your
     * collection*, which reads fine as a heading over a stack of panels and badly as a word
     * in a row of tabs: the other three name a thing you configure, and a tab strip is
     * scanned rather than read.
     */
    TOOLS("Tools", "lp-pane-tools"),

    /**
     * What is allowed to act on this collection as you, and how to stop it.
     *
     * **Last**, after *Tools*, and named for the list rather than for the protocol. "MCP"
     * means nothing to most people, and the switch is the least interesting thing here after
     * the first day: what somebody comes back for is the two lines to paste into an agent
     * and the ability to cut one off. Lunicle's equivalent section is called the same thing,
     * which is worth more than a name of our own.
     *
     * It is the odd one out in the same way *Tools* is, one step further along. *Tools* is
     * still about the collection; this is about *who else may touch it*, which is the only
     * thing on this window that is not a preference or an action of the operator's own.
     */
    CONNECTIONS("Connections", "lp-pane-connections"),
}

/**
 * The inside of a settings window.
 *
 * Built once and updated in place rather than repainted, for the reason `EditBookmarkView`
 * gives: there is a caret, a selection and a half-typed `#tag` with a menu open under it on
 * this surface, and a rebuild on every state would throw all three away. Every write into a
 * control checks first that the value genuinely differs, which is what makes the initial
 * fill work without fighting the person typing.
 *
 * @param model the window's backing view model. Every gesture goes to it and nothing is
 *   decided here.
 */
internal class SettingsView(private val model: SettingsBackingViewModel) {

    /** What the toolkit mounts. */
    val root: HTMLElement = element("div", "lp-settings")

    private val loadingPanel = element("div", "lp-settings-loading", "Loading…")
    private val failedPanel = element("div", "lp-settings-failed")
    private val body = element("div", "lp-settings-body")

    // ── The tabs ─────────────────────────────────────────────────────────────

    private val tabButtons = mutableMapOf<Tab, HTMLButtonElement>()
    private val panes = mutableMapOf<Tab, HTMLElement>()

    // ── The provider block ───────────────────────────────────────────────────

    private val pill = element("span", "lp-pill")
    private val pillLabel = element("span", "lp-pill-label")

    private val testButton = button("Test connection", "lp-settings-secondary") {
        model.onTestConnectionRequested()
    }
    private val testResult = element("div", "lp-provider-result")

    // ── Behaviour ────────────────────────────────────────────────────────────

    private val summariesToggle = Toggle("Write summaries") { model.onSummariesToggled(it) }
    private val tagsToggle = Toggle("Add tags") { model.onTagsToggled(it) }

    // ── The three cards ──────────────────────────────────────────────────────

    private val lengthChoice = ChoiceRow(
        values = SummaryLengths.all,
        label = ::lengthLabel,
        onChosen = { model.onSummaryLengthChosen(it) },
    )

    /**
     * Language, as three buttons where it was a native `<select>`.
     *
     * The `<select>` was defended on the grounds that a browser draws a chevron, handles the
     * keyboard and positions a popover better than we would — all true, and all beside the
     * point once there are three options and lunula has no dropdown in it anywhere. A menu
     * that has to be opened to find out it holds three things is a worse control than three
     * things.
     */
    private val languageChoice = ChoiceRow(
        values = SummaryLanguages.all,
        label = ::languageLabel,
        onChosen = { model.onLanguageChosen(it) },
    )

    // ── The vocabulary ceiling, on the Tags tab ──────────────────────────────

    /**
     * How many tags this account wants to end up with, as five presets.
     *
     * **On the Tags tab rather than the AI one**, which is where its predecessor lived. That
     * predecessor was *New tags per bookmark*, eleven buttons from 0 to 10, and it was an AI
     * setting because the only thing that read it was the enrichment worker. This one is not:
     * it is a statement about the collection — how many distinct tags it should settle at —
     * and it is read by the tidy tool as well as by the worker. A number two features obey
     * belongs beside the vocabulary it acts on, not inside one of the things acting on it.
     *
     * Five buttons and no spinner, for the reason `TagVocabularyTargets` sets out: the useful
     * range is logarithmic and the difference between 180 and 200 is not one anybody can
     * perceive. *No limit* is last because an opt-out offered first reads as the
     * recommendation.
     */
    private val vocabularyChoice = ChoiceRow(
        values = TagVocabularyTargets.all,
        label = ::vocabularyLabel,
        onChosen = { model.onTagVocabularyTargetChosen(it) },
    )

    // ── Tidying, on the Vocabulary panel ─────────────────────────────────────
    //
    // A sentence about the collection and two buttons, of which the second is usually not
    // there. *Tidy…* opens a plan; *Undo last tidy* appears only while there is a snapshot to
    // go back to, which is a fact the server answers rather than something this window
    // remembers — so it is right after a reload, after a restart, and after a tidy applied
    // from a phone.
    //
    // Built once and hidden rather than rebuilt per state, exactly as the backfill's pair is
    // and for its reason: these repaint after every tidy and every undo, and a button replaced
    // under the pointer loses its hover and its focus.

    private val tidyDetail = element("div", "lp-panel-detail")

    /**
     * *Tidy…*, and the ellipsis is doing work.
     *
     * It says a dialog follows rather than an action — which is the whole promise of this
     * button, since nothing is removed until somebody has seen the preview and pressed *Apply*
     * inside it. Secondary rather than accent or destructive for the same reason: pressing this
     * destroys nothing at all.
     */
    private val tidyButton = button("Tidy…", "lp-settings-secondary lp-tidy-open") {
        model.onTidyRequested()
    }

    /**
     * *Undo last tidy*, in the same place, when there is one.
     *
     * Quiet rather than accented: it is the escape hatch, and an escape hatch drawn as the
     * recommended action reads as an instruction to take it. It disappears once pressed,
     * because the snapshot is spent by taking it — one step back, not a history.
     */
    private val undoTidyButton = button("Undo last tidy", "lp-settings-secondary lp-tidy-undo") {
        model.onUndoTidyRequested()
    }
    private val tidyMessage = element("div", "lp-panel-note")

    /**
     * What *Planning…* looks like while it lasts, which is now about ninety seconds.
     *
     * ── Why a sweep rather than a bar that fills ─────────────────────────────
     *
     * There is one request and one answer, and nothing comes back in between, so there is no
     * fraction to draw. A determinate bar would have to guess its own rate — and a guessed bar
     * always ends up sitting at ninety per cent for the last third of the wait, which teaches
     * somebody that this window's progress is decorative. An indeterminate sweep claims
     * exactly what is true: something is happening, and how much longer is not knowable from
     * here.
     *
     * The sentence beside it carries the honest part, and it comes from the view model rather
     * than being composed here — see `tidyProgressNote`, where the three stages of the wait are
     * argued. This is only where it is put on screen.
     */
    private val tidyProgressNote = element("div", "lp-panel-note lp-tidy-progress-note")
    private val tidyProgress = element("div", "lp-tidy-progress").also { row ->
        val track = element("div", "lp-tidy-progress-track")
        track.appendChild(element("div", "lp-tidy-progress-sweep"))
        row.appendChild(track)
        row.appendChild(tidyProgressNote)
        // Read out as it changes, because somebody who cannot see the sweep has nothing else
        // saying the press was received. `polite` rather than `assertive`: a line that
        // interrupts every eight seconds is worse than the silence it replaces.
        row.setAttribute("aria-live", "polite")
        row.hidden = true
    }

    /**
     * The open plan, or null when there is none.
     *
     * The one piece of DOM on this surface with a lifetime of its own: it lives on `<body>`
     * rather than inside the window, because a modal that were a child of a draggable pane
     * would be clipped by it and would move with it. Created when the state first carries a
     * plan and disposed the moment it stops — so "a dialog is up" cannot disagree with
     * "the state has one", which is the bug `SignInView` routes its dismissal through the view
     * model to avoid.
     */
    private var tidyDialog: TidyTagsDialog? = null

    // ── Backfill, and the panel ──────────────────────────────────────────────
    //
    // Two buttons in one place, one of them hidden — the same swap `EditBookmarkView` makes
    // for its delete confirmation, and for the same reason: the panel has one row and two
    // states, *start this* and *stop it*.
    //
    // Built once and hidden rather than rebuilt per state, which is where this differs from
    // the editor's footer. That footer swaps three controls for two and can afford to throw
    // them away; here the accent button is the one thing on this window somebody may be
    // holding a pointer over while a poll two seconds later repaints, and a button replaced
    // under the cursor loses its hover and its focus.

    private val backfillDetail = element("div", "lp-panel-detail")
    private val backfillButton = button("Backfill", "lp-settings-accent") {
        model.onBackfillRequested()
    }

    /**
     * *Cancel*, shown in the accent button's place while the queue is not empty.
     *
     * Quiet rather than accented or dangerous: it stops work, it destroys nothing, and it is
     * undone by pressing the button it replaced. `lp-backfill-cancel` alongside the shared
     * button class so a test — and a future style — can address this one rather than the
     * three other secondary buttons on the window.
     */
    private val cancelBackfillButton =
        button("Cancel", "lp-settings-secondary lp-backfill-cancel") {
            model.onCancelBackfillRequested()
        }
    private val backfillMessage = element("div", "lp-panel-note")

    /** The server's own sentence about the last refused write, or nothing. */
    private val message = element("div", "lp-settings-message")

    // ── The two tag lists ────────────────────────────────────────────────────

    /**
     * The list as text, not as chips — [TagListField], and its header argues the change.
     *
     * These two were `TagInputField`, the same chip control the edit window and the list
     * window use, and they are the one place it was the wrong shape. **A chip cannot be
     * moved.** That cost nothing while these were sets, and it started costing the moment the
     * list window's tag rail began drawing *Offer by default* in the order it is given: the
     * account's order became a thing worth choosing, and with chips the only way to choose it
     * was to rename a tag to something earlier in the alphabet.
     *
     * The menu is unchanged and still here — it is the spelling aid, and nothing about
     * completing `#ru` to `#rust` needed a chip to hang on.
     */
    private val defaultTagsField = TagListField(
        placeholder = "#tag #tag",
        onEdited = { model.onDefaultTagsEdited(it) },
    )

    /**
     * The same control again, over the list the one above is a subset of.
     *
     * Two fields rather than one field with a per-tag on/off, and the reason survives the
     * change from chips to text intact: *offered* and *offered and already pressed* are two
     * lists, and a single field would need a second mark per tag to say which of the two a
     * word is in. The invariant that keeps them coherent lives in the view model and in the
     * store, not in the widget — see `SettingsBackingViewModel.onQuickTagsEdited`.
     */
    private val quickTagsField = TagListField(
        placeholder = "#tag #tag",
        onEdited = { model.onQuickTagsEdited(it) },
    )

    // Two toggles were here — *Close the popup after saving* and *Warn when a URL is already
    // saved* — and both are gone rather than moved. Neither ever changed anything: the popup
    // has always closed itself two seconds after a save, and a re-save now says so in its own
    // sentence on that same notice. A switch over a behaviour that does not vary is a
    // question with one answer, and the person reading it has to try both to find that out.

    // ── Connections ──────────────────────────────────────────────────────────
    //
    // The switch, the two things to copy, and the list. Built once and painted from
    // `State.Ready.mcp` like everything else here — including the URL, which is the one
    // string on this window that is **not** derived from `window.location`. It is computed by
    // the server from the origin the request actually arrived on, because a deployment may
    // sit behind a reverse proxy and a wrong URL here is a copied command that fails only on
    // the machine of the person who pasted it. See `McpState.serverUrl`.

    private val agentAccessToggle = Toggle(AGENT_ACCESS_LABEL) { model.onAgentAccessToggled(it) }

    private val serverUrlCode = element("code", "lp-agent-url")
    private val serverUrlCopy = CopyButton("lp-agent-copy-url") { lastServerUrl }
    private val commandCode = element("code", "lp-agent-url lp-agent-command")
    private val commandCopy = CopyButton("lp-agent-copy-command") { lastInstallCommand }

    /**
     * The switch, the URL and the list, in one wrapper.
     *
     * Held as a field so [renderConnections] can hide the whole of it in one line when the
     * server would not answer — exactly one of this and [agentUnavailable] is ever on screen.
     */
    private val agentColumn = element("div", "lp-settings-stack lp-agent-stack")

    /** Where the rows go. Rebuilt only when the list itself changes — see [renderConnections]. */
    private val connectionList = element("div", "lp-agent-list")

    /** The *Revoke* buttons currently in [connectionList], so a write in flight can grey them. */
    private var revokeButtons: List<HTMLButtonElement> = emptyList()

    /**
     * What the list says when there is nothing in it.
     *
     * A sentence rather than an empty box, because **empty is the common case** and a panel
     * with nothing in it reads as one that has not finished loading. It also says how a row
     * would get here, which is the only useful thing to tell somebody looking at an empty
     * list: nothing on this screen can put one there.
     */
    private val connectionsEmpty = element(
        "div",
        "lp-panel-detail lp-agent-empty",
        "Nothing is connected. An agent appears here after you approve it in the browser " +
            "window it opens — there is no button here that can start that.",
    )

    private val agentMessage = element("div", "lp-panel-note lp-agent-message")

    /**
     * What the pane says when the server would not answer at all.
     *
     * Shown *instead of* the switch, the URL and the list rather than beside them. Drawing an
     * *Off* switch over a request that failed would be telling somebody nothing can act as
     * their account, which is a claim about a security gate that nothing here has checked.
     */
    private val agentUnavailable = element(
        "div",
        "lp-panel-detail lp-agent-unavailable",
        "Could not read your connections. Reopen this window to try again.",
    )

    /** The last state's URL and command, so the copy buttons carry what is on screen. */
    private var lastServerUrl = ""
    private var lastInstallCommand = ""

    /** What [connectionList] currently holds, so an unchanged list is not rebuilt under a cursor. */
    private var renderedConnections: List<State.Mcp.Connection> = emptyList()

    private val origin = window.location.origin
    private val bookmarkletUrl = bookmarkletHref(origin)

    /** The plain bookmark for the pages the one above cannot run on. See [saveLinkHref]. */
    private val saveLinkUrl = saveLinkHref(origin)

    /**
     * *Copy*, for the raw bookmarklet URL.
     *
     * The same [CopyButton] the Connections tab's two use, rather than a hand-rolled one:
     * three buttons that say *Copied* for a moment should agree about how long a moment is,
     * and this one predates the other two by a ticket.
     */
    private val copyButton = CopyButton(value = { bookmarkletUrl })

    init {
        root.appendChild(loadingPanel)
        root.appendChild(body)
        root.appendChild(failedPanel)
        buildBody()
    }

    /**
     * Paint one state.
     *
     * @param state the whole of what to show. Exactly one of its fields is non-null, and
     *   this function's shape is that fact: three panels, one shown.
     */
    fun render(state: State) {
        loadingPanel.hidden = state.loading == null
        body.hidden = state.ready == null
        failedPanel.hidden = state.failed == null

        state.failed?.let { failedPanel.textContent = it.message }
        state.ready?.let { renderReady(it) }
    }

    // ── Building ─────────────────────────────────────────────────────────────

    /**
     * The window: a refusal line, a tab strip, and four panes of which one shows.
     *
     * Every pane is built once, here, and then only hidden — not built on the way in. Two
     * reasons, and the second is the one that matters. The cheap reason is that there is
     * nothing to fetch per tab: [render] paints the whole of `State.Ready` into all four
     * whether they are on screen or not, so a tab switch is a `hidden` flag and never a
     * request. The real reason is that a pane rebuilt on selection would throw away the
     * `#tag` field's caret, its selection and any half-typed tag with a menu open under it —
     * which is the same argument this class's own header makes about repainting, and it
     * would be defeated by a control that rebuilt on a click somewhere else.
     */
    private fun buildBody() {
        // The refusal line sits outside every pane, above the strip. It is the server's
        // sentence about a write that did not land, and the writes that can actually be
        // refused are all in one tab — so painting it inside that tab would mean a person
        // who pressed something, got refused, and switched away never learns it failed.
        // Hidden whenever there is nothing to say, so it costs no space in the ordinary case.
        body.appendChild(message)
        body.appendChild(buildTabs())

        panes[Tab.BOOKMARKLET] = buildBookmarklet()
        panes[Tab.TAGS] = buildTags()
        panes[Tab.AI] = buildAi()
        panes[Tab.TOOLS] = buildTools()
        panes[Tab.CONNECTIONS] = buildConnections()
        Tab.entries.forEach { tab ->
            val pane = panes.getValue(tab)
            // A second class naming which tab this is, added here rather than in each
            // builder so the four cannot drift from the enum. Nothing styles it — it is for
            // anything that has to address one pane, which so far is only the tests.
            pane.classList.add(tab.paneClass)
            body.appendChild(pane)
        }

        selectTab(Tab.BOOKMARKLET)
    }

    /**
     * The strip, in [Tab]'s own order.
     *
     * Underline tabs rather than a row of boxed buttons, which is what Lunicle's settings
     * pane draws and is a deliberate difference from the choice buttons *inside* the panes:
     * a tab is where you are, a choice button is what you picked, and the two must not look
     * alike on one screen.
     */
    private fun buildTabs(): HTMLElement {
        val strip = element("div", "lp-settings-tabs")
        Tab.entries.forEach { tab ->
            val tabButton = button(tab.label, "lp-settings-tab") { selectTab(tab) }
            tabButtons[tab] = tabButton
            strip.appendChild(tabButton)
        }
        return strip
    }

    /**
     * Show one pane and light one tab.
     *
     * Which tab is showing is **view-local and not persisted**, unlike the window itself.
     * The workspace remembers that a settings window is open and where it sits; that it was
     * last left on *Tools* is not a fact worth keeping, because nobody opens settings to
     * return to where they were — they open it to do the one thing they came for, and
     * landing on the bookmarklet is the better default every time.
     */
    private fun selectTab(tab: Tab) {
        panes.forEach { (which, pane) -> pane.hidden = which != tab }
        tabButtons.forEach { (which, tabButton) ->
            tabButton.classList.toggle("lp-settings-tab-on", which == tab)
        }
    }

    /** The AI tab: the provider, the two switches, the three cards. */
    private fun buildAi(): HTMLElement {
        val section = element("section", "lp-settings-pane")
        section.appendChild(
            heading(
                "AI",
                "What happens to a page when you save it. The key comes from the server's " +
                    "environment; this is what it gets spent on.",
            ),
        )
        val main = element("div", "lp-settings-stack")
        main.appendChild(buildProvider())
        main.appendChild(buildBehaviour())
        main.appendChild(buildCards())
        section.appendChild(main)
        return section
    }

    /**
     * Status, not a form.
     *
     * Two facts and a button: which service, and which variable the server reads its key
     * from — then *Test connection*, which is the only thing on this panel that does
     * anything. There was a third fact, a *Model* field, and it is gone; see the file header.
     *
     * No caption under the button either. It read *"Read from the environment at boot.
     * Nothing to enter, nothing stored in the browser."*, which is three ways of saying what
     * the padlock, the variable name and the `SET ON THE SERVER` badge two lines above
     * already say. A sentence that restates the control beside it makes a screen longer
     * without making it clearer.
     */
    private fun buildProvider(): HTMLElement {
        val panel = element("section", "lp-panel lp-provider")

        val header = element("div", "lp-provider-head")
        header.appendChild(element("span", "lp-panel-title", "Provider"))
        pill.appendChild(element("span", "lp-pill-dot"))
        pill.appendChild(pillLabel)
        header.appendChild(pill)
        panel.appendChild(header)

        val fields = element("div", "lp-provider-fields")

        // One provider, drawn as a settled fact rather than as a live dropdown with one
        // option in it. Adding a second is out of scope for this epic, and a control that
        // offers one choice is a control that reads as broken.
        fields.appendChild(
            field("Service", element("div", "lp-provider-static", "OpenAI"), "lp-provider-service"),
        )

        // **Not an input.** There is nothing to type: the key is read from the environment at
        // boot, is never stored anywhere, and no route returns it or any part of it. What is
        // drawn is the name of the variable, recessed, with a padlock and a badge — a fact
        // about the deployment rather than a field somebody could put a secret into.
        val keyRow = element("div", "lp-provider-key")
        keyRow.appendChild(element("span", "lp-provider-lock", "🔒"))
        keyRow.appendChild(element("code", "lp-provider-variable", KEY_VARIABLE))
        keyRow.appendChild(element("span", "lp-provider-badge", "SET ON THE SERVER"))
        fields.appendChild(field("API key", keyRow, "lp-provider-keyfield"))

        panel.appendChild(fields)

        val footer = element("div", "lp-provider-foot")
        footer.appendChild(testButton)
        panel.appendChild(footer)
        panel.appendChild(testResult)
        return panel
    }

    /** Two switches. There is no third — see the file header. */
    private fun buildBehaviour(): HTMLElement {
        val panel = element("section", "lp-panel lp-switches")
        panel.appendChild(
            summariesToggle.row("Two-line preview in the list, full text when a row is expanded."),
        )
        panel.appendChild(
            tagsToggle.row(
                "Existing tags are applied first, then new ones only if nothing established " +
                    "fits. Nothing waits for review.",
            ),
        )
        return panel
    }

    /**
     * The two choices, stacked rather than sat side by side.
     *
     * They were three cards in a wrapping row, which worked while each held one compact
     * control — a track, a three-way segment, a dropdown. A button row is as wide as its
     * options, and the widest of them was the new-tag cap's eleven, so a card that had to
     * hold it was no longer the same size as its neighbours. A column keeps every row on one
     * line at any window width, and it is what the toolkit's own settings sidebar does with
     * the same controls.
     *
     * There were three, and the third — *New tags per bookmark* — is on the Tags tab now, as
     * the vocabulary ceiling that replaced it. See [vocabularyChoice]. The column survives
     * the loss because it was never about the count of cards.
     */
    private fun buildCards(): HTMLElement {
        val cards = element("div", "lp-cards")
        cards.appendChild(
            card(
                title = "Summary length",
                control = lengthChoice.root,
                caption = "Short is roughly 40 words — two lines in a list row.",
            ),
        )
        cards.appendChild(
            card(
                title = "Language",
                control = languageChoice.root,
                caption = "A Swedish article gets a Swedish summary.",
            ),
        )
        return cards
    }

    /**
     * *Tags*, its own section — the ceiling, then the two lists, at the window's width.
     *
     * It was a 17rem column wedged beside the draggable bookmarklet, under a mono label
     * reading `DEFAULTS FOR NEW SAVES`, and the move out of there is the point rather than
     * the styling. Two things were wrong with it. It was **narrower than everything else on
     * the surface**, so a `#tag` field with six chips in it wrapped to four rows while the
     * panels above ran the full measure — one column of settings drawn to two widths reads
     * as an accident. And it was **filed under the bookmarklet**, which stopped being true:
     * *Offer by default* is now read by the list window's tag rail as well, so the same
     * setting decides what a popup offers and what the rail leads with. A setting two
     * surfaces obey cannot live inside one of them.
     *
     * ── Two lines under two fields, and they used to be four ────────────────
     *
     * Both details were a paragraph, and both spent it restating the lede above them. The
     * lede said what the section is for — *kept within reach, offered as chips, first in the
     * rail* — so a field caption repeating it is the reader being told the same thing twice
     * before they reach the control. What is left is the one sentence each field needs that
     * the other one does not: **where they show up**, and **that one of the two lists starts
     * pressed**. Everything else was elaboration on a control anybody can see.
     *
     * ── Two panels now, and the lede had to give way ─────────────────────────
     *
     * [buildVocabulary] joined this tab above the lists, so the section lede can no longer be
     * about them alone: it names both halves and each panel carries its own title. The
     * ordering is largest-scope-first — how many tags there should be, then which handful of
     * them is kept in reach — and it is also the order somebody meets them, since a ceiling
     * is a decision you make once and a shortlist is one you revise.
     */
    private fun buildTags(): HTMLElement {
        val section = element("section", "lp-settings-pane")
        section.appendChild(
            heading(
                "Tags",
                "How large a vocabulary you want to keep, and the handful of it you file " +
                    "under most.",
            ),
        )
        section.appendChild(buildVocabulary())

        val panel = element("section", "lp-panel lp-defaults")
        panel.appendChild(element("div", "lp-panel-title", "Within reach"))
        // Offered first, applied second, which is the order they compose in: the second
        // list is a subset of the first, and reading them the other way round asks somebody
        // to hold a rule in their head before they have met the thing it is about.
        panel.appendChild(field("Offer by default", quickTagsField.root))
        panel.appendChild(
            element(
                "div",
                "lp-panel-detail",
                "Chips in the save popup, and first in a tag rail — in this order.",
            ),
        )
        panel.appendChild(field("Apply by default", defaultTagsField.root))
        // No longer "press to drop one": there is no chip to press. Both lists are text now,
        // because the order above is read by the tag rail and a chip cannot be moved.
        panel.appendChild(
            element("div", "lp-panel-detail", "The same tags, already pressed on a new save."),
        )
        section.appendChild(panel)
        return section
    }

    /**
     * *Vocabulary* — how many distinct tags this collection should settle at, and the tool
     * that gets it there.
     *
     * A panel of its own rather than a card in a row, because it is the first thing on the tab
     * and because it is where the tools that act on the vocabulary belong. That last clause
     * used to be a promise about a button that did not exist yet; the button is here now, on
     * the same panel as the number it works towards, which is the whole reason the ceiling was
     * moved to this tab in the first place.
     *
     * ── What the caption says, and what it deliberately does not ─────────────
     *
     * It says the ceiling is a target rather than a wall, because that is the surprising
     * half: a bookmark nothing established files still coins a tag when the collection is at
     * its ceiling, and somebody who read this as a hard stop would file a bug the first time
     * they saw the count go past it. See `EnrichmentWorker.tagsToApply`.
     *
     * ── And the count, which was deliberately absent and now is not ──────────
     *
     * The note that stood here said a count with no action beside it is a statistic, and a
     * settings window is not a dashboard. That was right while there was nothing to press.
     * *1182 tags across 1293 bookmarks. 685 used once.* sits on the same row as *Tidy…*
     * precisely because it is the sentence that makes the button worth pressing — the number
     * and the action are one thought, and either without the other is half of it.
     */
    private fun buildVocabulary(): HTMLElement {
        val panel = element("section", "lp-panel lp-vocabulary")
        panel.appendChild(element("div", "lp-panel-title", "Vocabulary"))
        panel.appendChild(vocabularyChoice.root)
        panel.appendChild(
            element(
                "div",
                "lp-panel-detail",
                "How many distinct tags to aim for. At the target, new tags are only " +
                    "invented for a bookmark nothing you already use fits.",
            ),
        )

        // The same row layout the two Tools panels use — a paragraph on the left and a button
        // pushed right — because it is the same shape: a sentence about the collection and one
        // press that acts on all of it. Its own class so a selector can address this row
        // rather than answering with whichever `.lp-backfill-row` comes first.
        val row = element("div", "lp-backfill-row lp-tidy-row-panel")
        val text = element("div", "lp-backfill-text")
        text.appendChild(tidyDetail)
        row.appendChild(text)
        row.appendChild(tidyButton)
        row.appendChild(undoTidyButton)
        panel.appendChild(row)
        // Under the row rather than inside it, so the sweep spans the panel and the button
        // stays where it was: a control that moves while it is being waited on is a control
        // somebody clicks twice.
        panel.appendChild(tidyProgress)
        panel.appendChild(tidyMessage)
        return panel
    }

    /**
     * *Tools* — the bulk actions, which is the backfill and, for now, only the backfill.
     *
     * A tab of its own rather than a panel on **AI**, because what is filed here is a kind of
     * control unlike anything else in this window: **one press that does something to every
     * bookmark at once**, rather than a preference that changes what the next save does. It
     * held two of them until the SheetMark import was retired, and it keeps the stack it had
     * so that the next bulk action is an `appendChild` rather than a re-layout.
     */
    private fun buildTools(): HTMLElement {
        val section = element("section", "lp-settings-pane")
        section.appendChild(
            heading(
                "Tools",
                "Things that act on everything you have already saved. They run in the " +
                    "background; nothing here has to be watched.",
            ),
        )
        val column = element("div", "lp-settings-stack")
        column.appendChild(buildBackfill())
        section.appendChild(column)
        return section
    }

    /**
     * *Backfill* — the panel that starts hours of work, and the one that says how it is going.
     *
     * One panel with two states rather than a second panel that appears while something is
     * running: it is the same subject, and a progress line that arrived somewhere else on the
     * tab would leave the button above it still offering to queue work that is already
     * queued.
     */
    private fun buildBackfill(): HTMLElement {
        val panel = element("section", "lp-panel lp-backfill")
        val row = element("div", "lp-backfill-row")
        val text = element("div", "lp-backfill-text")
        text.appendChild(element("div", "lp-panel-title", "Backfill"))
        text.appendChild(backfillDetail)
        row.appendChild(text)
        row.appendChild(backfillButton)
        row.appendChild(cancelBackfillButton)
        panel.appendChild(row)
        panel.appendChild(backfillMessage)
        return panel
    }

    /**
     * *Connections* — the switch, the two lines to paste into an agent, and what is
     * connected.
     *
     * ── The switch off does not hide the rest ────────────────────────────────
     *
     * The obvious design reveals the URL only once agent access is on. It is wrong twice
     * over. It makes the setup order *flip a switch you have no reason to trust yet, then go
     * looking for the command* — and the command is what most people open this tab for. And
     * hiding the **list** would hide the one state somebody urgently needs to see: an agent
     * connected while the switch is off is exactly what a person turns the switch off in
     * order to go and revoke.
     *
     * ── There is no *Connect* button, and there must never be ────────────────
     *
     * The OAuth flow is always started by the agent: the agent has to be the client, hold the
     * PKCE verifier and receive the code at its own redirect URI. This tab only ever displays
     * a URL and lists the result. A *Connect to Claude Code* button cannot be built, and if
     * one appears in a later design somebody has misunderstood which direction the protocol
     * runs in. See the server's `McpApiRoutes.kt`.
     */
    private fun buildConnections(): HTMLElement {
        val section = element("section", "lp-settings-pane")
        section.appendChild(
            heading(
                "Connections",
                "What is allowed to act on your bookmarks as you, and how to stop it.",
            ),
        )

        // `lp-agent-switch` rather than the AI tab's `lp-switches`, though the two are drawn
        // identically. That class is what several tests use to mean *the two behaviour
        // switches*, and a third row answering those selectors would make them assert
        // something they do not say.
        val switchPanel = element("section", "lp-panel lp-agent-switch")
        switchPanel.appendChild(agentAccessToggle.row(AGENT_ACCESS_EXPLANATION))
        agentColumn.appendChild(switchPanel)

        agentColumn.appendChild(buildAgentSetup())

        val list = element("section", "lp-panel lp-agent-connections")
        list.appendChild(element("div", "lp-panel-title", "Connected agents"))
        list.appendChild(connectionsEmpty)
        list.appendChild(connectionList)
        agentColumn.appendChild(list)

        agentColumn.appendChild(agentMessage)
        section.appendChild(agentColumn)
        // Beside the column rather than inside it, because it stands in the column's place:
        // exactly one of the two is on screen. See [agentUnavailable].
        section.appendChild(agentUnavailable)
        return section
    }

    /**
     * The two things to copy, in the order they are used.
     *
     * The command first would be tidier and is the wrong way round: the URL is the fact, and
     * the command is one client's way of spelling it. Somebody wiring up an agent that is not
     * Claude Code needs the first and cannot use the second, and a surface that leads with a
     * vendor's command line reads as though it were the only supported one.
     *
     * Both are `<code>` filled in [renderConnections] rather than at build time — they are
     * the **server's** answer, not this page's guess. See [buildConnections].
     */
    private fun buildAgentSetup(): HTMLElement {
        val panel = element("section", "lp-panel lp-agent-setup")
        panel.appendChild(element("div", "lp-panel-title", "Server URL"))

        val urlRow = element("div", "lp-bookmarklet-raw lp-agent-row")
        urlRow.appendChild(serverUrlCode)
        urlRow.appendChild(serverUrlCopy.root)
        panel.appendChild(urlRow)
        panel.appendChild(
            element(
                "div",
                "lp-panel-detail",
                "The address an MCP client connects to. It is worked out by the server from " +
                    "the address your browser reached it on, so it is right behind a proxy " +
                    "and right on a deployment.",
            ),
        )

        panel.appendChild(element("div", "lp-settings-mono-label", "IN CLAUDE CODE"))
        val commandRow = element("div", "lp-bookmarklet-raw lp-agent-row")
        commandRow.appendChild(commandCode)
        commandRow.appendChild(commandCopy.root)
        panel.appendChild(commandRow)
        panel.appendChild(
            element(
                "div",
                "lp-panel-detail",
                "Run it once. A browser window opens, you approve it here, and from then on " +
                    "the agent works this collection as you — there is no key to mint and " +
                    "nothing to paste into a config file.",
            ),
        )
        return panel
    }

    private fun buildBookmarklet(): HTMLElement {
        val section = element("section", "lp-settings-pane")
        section.appendChild(
            heading(
                "The bookmarklet",
                "Drag this button to your bookmarks bar. Pressing it on any page opens the " +
                    "save popup for that page, signed in as you — no extension, no " +
                    "permissions prompt.",
            ),
        )

        // One column, not two. The tag lists that used to sit to the right of this are a tab
        // of their own — see [buildTags] — which leaves the dashed drop zone as the whole of
        // this pane rather than as one half of a row.
        val drop = element("div", "lp-bookmarklet-drop")
        // An anchor with a `javascript:` href, because that is the only thing a browser lets
        // you drag onto the bookmarks bar. Its href is set as a property rather than written
        // into markup, so there is no attribute to escape and no way for the origin to end up
        // parsed as anything but a URL.
        val link = document.createElement("a") as HTMLAnchorElement
        link.className = "lp-settings-bookmarklet"
        link.href = bookmarkletUrl
        link.draggable = true
        // The product's own mark, not a pushpin emoji. This is the one place somebody is
        // asked to put LunaPin's name on their own bookmarks bar, and it should be LunaPin's
        // icon that goes there — see [lunaPinMark], which also says why it is drawn inline.
        link.appendChild(lunaPinMark(size = 16, gradientId = "lp-mark-bookmarklet"))
        link.appendChild(element("span", "lp-settings-bookmarklet-label", "Save to LunaPin"))
        // Pressing it here would run the bookmarklet against LunaPin's own page, which saves
        // LunaPin to LunaPin. The note below says so; this makes the click harmless rather
        // than merely explained.
        link.addEventListener("click", { it.preventDefault() })
        drop.appendChild(link)
        // No drawn bookmarks bar under it, and no arrow pointing at one. There used to be
        // both: a dashed strip labelled *drop here*, meant as a picture of the target. The
        // target is the browser's own bar, which is above this page and outside it — so the
        // one directional cue on the surface pointed away from where the gesture ends, and a
        // strip drawn at the scale of the real controls around it reads as a drop zone the
        // page will handle rather than as an illustration. The heading says where to drag
        // it and `cursor: grab` says it is draggable; a picture that can be mistaken for a
        // control is worse than no picture.

        drop.appendChild(element("div", "lp-settings-mono-label", "IF DRAGGING IS AWKWARD"))
        val rawRow = element("div", "lp-bookmarklet-raw")
        rawRow.appendChild(element("code", "lp-bookmarklet-url", bookmarkletUrl))
        rawRow.appendChild(copyButton.root)
        drop.appendChild(rawRow)
        drop.appendChild(
            element(
                "div",
                "lp-panel-detail",
                "Create a bookmark by hand and paste this as its address.",
            ),
        )

        // Assembled from three nodes rather than one string because the origin is <code> and
        // the sentence around it is not.
        val note = element("p", "lp-settings-note")
        note.appendChild(document.createTextNode("This one saves to "))
        note.appendChild(element("code", "lp-settings-origin", origin))
        note.appendChild(
            document.createTextNode(
                ", because that is the LunaPin that served this page. Clicking it here does " +
                    "nothing — drag it to the bar first.",
            ),
        )
        drop.appendChild(note)
        section.appendChild(drop)
        section.appendChild(buildSaveLink())
        return section
    }

    /**
     * The second thing to drag: an ordinary bookmark, for the pages the first one cannot
     * touch.
     *
     * ── Why a browser can refuse a bookmarklet outright ──────────────────────
     *
     * A `javascript:` URL runs in the visited page's context, and some pages have no such
     * context to lend. A document served with `Content-Security-Policy: sandbox` has an
     * opaque origin, and a `javascript:` URL has to inherit one — so Chrome refuses to run
     * it there whether or not the header says `allow-scripts`. That is how an issue tracker
     * serves an uploaded HTML attachment so that reading it is safe. Chrome's built-in PDF
     * viewer is the other common one. On those the bookmarklet above is not
     * blocked halfway through; it never starts, there is no error and no popup, and the
     * button reads as broken. LPN-40 was reported against exactly such a page.
     *
     * Nothing in a bookmarklet can fix that, so this is a *different kind of bookmark*: a
     * plain link to LunaPin's own save form, which is a navigation and therefore always
     * allowed. What it gives up is the one thing the bookmarklet was for — it cannot read
     * the address or the title of the page you were on — so the form asks for the address
     * instead, and the sentence under it says to copy that first.
     *
     * Drawn as a second dashed block rather than a second button inside the first, because
     * the two are dragged to the same bar and dropped as two separate bookmarks; a shared
     * box would read as one thing with two halves.
     */
    private fun buildSaveLink(): HTMLElement {
        val drop = element("div", "lp-bookmarklet-drop lp-bookmarklet-fallback")
        drop.appendChild(element("div", "lp-settings-mono-label", "IF A PAGE WON'T RUN IT"))
        drop.appendChild(
            element(
                "div",
                "lp-panel-detail",
                "Some pages refuse to run any bookmarklet — Chrome's PDF viewer, and " +
                    "attachment or report previews served in a sandbox. Pressing the button " +
                    "above does nothing at all there, and no page can change that. Drag this " +
                    "one to the bar as well: copy the address first, press it, and paste.",
            ),
        )
        // A plain href, not a `javascript:` one — see [saveLinkHref]. Set as a property for
        // the reason the bookmarklet's is: no attribute to escape, no way for the origin to
        // be parsed as anything but a URL.
        val link = document.createElement("a") as HTMLAnchorElement
        link.className = "lp-settings-bookmarklet lp-settings-bookmarklet-quiet"
        link.href = saveLinkUrl
        link.draggable = true
        link.appendChild(lunaPinMark(size = 16, gradientId = "lp-mark-save-link"))
        link.appendChild(element("span", "lp-settings-bookmarklet-label", "Save a link"))
        // Unlike the bookmarklet, clicking this one *here* is harmless and even useful —
        // it is the save form, and it is the only way to try it without dragging anything.
        // It opens in a tab of its own so the settings window is still there afterwards.
        link.target = "_blank"
        link.rel = "noopener"
        drop.appendChild(link)
        return drop
    }

    // ── Painting ─────────────────────────────────────────────────────────────

    private fun renderReady(ready: State.Ready) {
        renderProvider(ready)
        renderBehaviour(ready)
        renderCards(ready)
        renderBackfill(ready)
        renderTagLists(ready)
        renderVocabulary(ready)
        renderConnections(ready)

        message.textContent = ready.message ?: ""
        message.hidden = ready.message == null
    }

    /**
     * The Vocabulary panel's sentence, its two buttons, and the modal that may be over them.
     *
     * ── The dialog is mounted from here, and disposed from here ──────────────
     *
     * There is exactly one place that opens it and one that closes it, and both are this
     * function reading `ready.tidyDialog`. Nothing else in the view holds a flag about whether
     * a modal is up, so "a dialog is in the document" cannot disagree with "the state has a
     * plan" — which is the same discipline `SignInView` keeps by routing every dismissal back
     * through its model rather than letting a dialog remove itself.
     */
    private fun renderVocabulary(ready: State.Ready) {
        tidyDetail.textContent = vocabularyDetail(ready)

        tidyButton.textContent = if (ready.isPlanningTidy) "Planning…" else "Tidy…"
        // Disabled while a plan is being worked out, and while an undo is in flight — undoing
        // is about to change the very vocabulary a plan would be about. Never disabled for
        // having nothing to tidy: whether there is anything is what pressing it finds out, and
        // a button that pre-judged that would need the plan it is refusing to ask for.
        tidyButton.disabled = ready.isPlanningTidy || ready.isUndoingTidy

        // Present only while the server says there is a snapshot. Not a flag this window sets
        // when it applies something: that would be wrong after a reload, wrong in a second
        // window, and wrong for a tidy applied on a phone.
        undoTidyButton.hidden = !ready.tidy.canUndo
        undoTidyButton.textContent = if (ready.isUndoingTidy) "Undoing…" else "Undo last tidy"
        undoTidyButton.disabled = ready.isUndoingTidy || ready.isPlanningTidy

        // Null is "no plan is being made", which is the same question `isPlanningTidy` answers —
        // asked of the note so the view has one source for both halves of the row rather than a
        // flag deciding whether to show a sentence composed somewhere else.
        val progress = ready.tidyProgressNote
        tidyProgress.hidden = progress == null
        tidyProgressNote.textContent = progress ?: ""

        tidyMessage.textContent = ready.tidyMessage ?: ""
        tidyMessage.hidden = ready.tidyMessage == null

        val plan = ready.tidyDialog
        if (plan == null) {
            tidyDialog?.dispose()
            tidyDialog = null
            return
        }
        val open = tidyDialog ?: TidyTagsDialog(model).also {
            tidyDialog = it
            it.mount(plan)
        }
        open.render(plan)
    }

    /**
     * *1182 tags across 1293 bookmarks. 685 used once.*
     *
     * The sentence the *Tidy…* button exists for. The singleton count is the headline finding
     * of this whole feature — on the collection it was measured against, 58% of the vocabulary
     * was used exactly once — and it is the number that tells somebody whether pressing the
     * button is worth a minute of their time.
     *
     * Zero singletons says so in words rather than printing *0 used once*, which reads as a
     * missing number. An account with no tags at all gets a sentence about that instead: the
     * count and the button are about a vocabulary, and there is not one yet.
     */
    private fun vocabularyDetail(ready: State.Ready): String {
        val status = ready.tidy
        if (status.tags == 0L) {
            return "No tags yet. They arrive as you save and as bookmarks are read."
        }
        val head = "${status.tags} ${if (status.tags == 1L) "tag" else "tags"} across " +
            "${status.bookmarks} ${if (status.bookmarks == 1L) "bookmark" else "bookmarks"}."
        val tail = when (status.singletons) {
            0L -> "None used only once."
            1L -> "1 used once."
            else -> "${status.singletons} used once."
        }
        return "$head $tail"
    }

    /**
     * The Connections tab: the switch, the two copyables, and the list.
     *
     * ── Null is a fourth state, not "off with nothing connected" ─────────────
     *
     * `ready.mcp` is null when the server would not answer. Everything else on this window
     * falls back to zeroes, which is honest because a count of nothing is a truthful reading
     * of a fresh account. This is a **security gate**, and an *Off* switch drawn over a
     * failed request would tell somebody nothing can act as their account without anything
     * having checked. So the whole column is swapped for a sentence.
     */
    private fun renderConnections(ready: State.Ready) {
        val mcp = ready.mcp
        agentColumn.hidden = mcp == null
        agentUnavailable.hidden = mcp != null
        if (mcp == null) return

        agentAccessToggle.set(mcp.isEnabled)
        // Disabled only while a write is in flight, and never for being off: the switch is
        // how it gets turned back on. Note what is *not* disabled with it — the copy buttons
        // — because the URL is what somebody came for and it is true either way.
        agentAccessToggle.disable(ready.isMcpBusy)

        lastServerUrl = mcp.serverUrl
        lastInstallCommand = mcp.installCommand
        serverUrlCode.textContent = mcp.serverUrl
        commandCode.textContent = mcp.installCommand

        renderConnectionRows(mcp.connections, busy = ready.isMcpBusy)

        agentMessage.textContent = ready.mcpMessage ?: ""
        agentMessage.hidden = ready.mcpMessage == null
    }

    /**
     * The rows, rebuilt only when the list has actually changed.
     *
     * A revoke replaces the list, so the rows genuinely have to be rebuilt then. What must
     * not happen is a rebuild on every render — a state emission caused by the enrichment
     * poll two seconds later would otherwise replace a *Revoke* button under somebody's
     * pointer, losing its hover and its focus, which is the reason the backfill's pair is
     * hidden rather than rebuilt.
     *
     * The name is written with `textContent` and never as markup. It is **self-reported at an
     * unauthenticated endpoint** — anyone may register a client calling itself "Claude Code"
     * — so this is the one string on this window a stranger chose. The dates beside it are
     * what actually identify a grant, which is why they are not optional: two agents claiming
     * the same name must still be separable.
     */
    private fun renderConnectionRows(connections: List<State.Mcp.Connection>, busy: Boolean) {
        connectionsEmpty.hidden = connections.isNotEmpty()
        if (connections != renderedConnections) {
            renderedConnections = connections
            connectionList.textContent = ""
            val buttons = mutableListOf<HTMLButtonElement>()
            connections.forEach { connection ->
                // Destructive, and the only thing on this window that is — it deletes tokens,
                // and the agent has to go back through the browser to return. No confirmation
                // in front of it; see `onRevokeConnectionRequested`, where that is argued.
                val revoke = button("Revoke", "lp-settings-secondary lp-agent-revoke") {
                    model.onRevokeConnectionRequested(connection.clientId)
                }
                buttons += revoke
                connectionList.appendChild(connectionRow(connection, revoke))
            }
            revokeButtons = buttons
        }
        revokeButtons.forEach { it.disabled = busy }
    }

    /** One agent: what it calls itself, when it was connected and last used, and *Revoke*. */
    private fun connectionRow(
        connection: State.Mcp.Connection,
        revoke: HTMLButtonElement,
    ): HTMLElement {
        val row = element("div", "lp-backfill-row lp-agent-item")
        val text = element("div", "lp-backfill-text")
        text.appendChild(element("div", "lp-agent-name", connection.name))
        text.appendChild(element("div", "lp-panel-detail lp-agent-detail", connection.detail))
        row.appendChild(text)
        row.appendChild(revoke)
        return row
    }

    private fun renderProvider(ready: State.Ready) {
        // The pill answers the deployment's question and only that one: a key is set, or it
        // is not. Whether it *works* is what the button below is for.
        pillLabel.textContent = if (ready.isAiAvailable) "Connected" else "Not configured"
        pill.className = if (ready.isAiAvailable) "lp-pill lp-pill-on" else "lp-pill lp-pill-off"

        val test = ready.test
        testButton.textContent = if (test?.isRunning == true) "Testing…" else "Test connection"
        testButton.disabled = test?.isRunning == true
        testResult.hidden = test == null || test.isRunning
        if (test != null && !test.isRunning) {
            testResult.textContent = test.message
            testResult.className =
                if (test.ok) "lp-provider-result lp-provider-result-ok"
                else "lp-provider-result lp-provider-result-bad"
        }
    }

    private fun renderBehaviour(ready: State.Ready) {
        summariesToggle.set(ready.settings.aiSummariesEnabled)
        tagsToggle.set(ready.settings.aiTagsEnabled)
    }

    /**
     * Three rows, one lit button each — two of them on the AI tab and the vocabulary
     * ceiling on the Tags tab.
     *
     * Painted together despite sitting on different tabs, because what they have in common
     * is the thing that matters here: they are the whole of this window's fixed-set choices,
     * every pane is painted whether it is showing or not (see [build]), and a second painter
     * for one row would be a second place to forget one.
     *
     * No guards against writing a value a control already has, which every other painter on
     * this surface carries. They exist to keep a repaint from fighting somebody mid-gesture —
     * a caret, a selection, a slider handle under a finger — and a row of buttons has no
     * such state to lose. Setting a class it already holds is not an event.
     */
    private fun renderCards(ready: State.Ready) {
        vocabularyChoice.select(ready.settings.aiTagVocabularyTarget)
        lengthChoice.select(ready.settings.aiSummaryLength)
        languageChoice.select(ready.settings.aiLanguage)
    }

    /**
     * One panel, two states, and the state is not this window's opinion.
     *
     * **Which of the two shows comes from `isBackfillRunning`**, which the view model derives
     * from counts the server took — see `EnrichmentProgress`. That is what makes this panel
     * right for a window opened halfway through a backfill, for a browser reloaded since, and
     * for a backfill started on a phone; a flag set when this window pressed the button would
     * be wrong in all three, and it was.
     *
     * **Running counts down and shows no bar.** A bar needs a baseline — how many were queued
     * at the start — which nothing stores and which could not survive a restart if it did.
     * *3948 bookmarks left to read* is honest, and it is the number that actually falls.
     */
    private fun renderBackfill(ready: State.Ready) {
        val running = ready.isBackfillRunning
        backfillDetail.textContent =
            if (running) runningDetail(ready) else idleDetail(ready.awaitingSummary)
        renderBackfillButtons(ready)
        backfillMessage.textContent = ready.backfillMessage ?: ""
        backfillMessage.hidden = ready.backfillMessage == null
    }

    /**
     * What is left, and what was given up on.
     *
     * The failure count rides beside the remaining one rather than in a panel of its own,
     * because it is the same run: a backfill that quietly gave up on twelve pages has
     * finished, and *finished* with nothing said about them is how somebody concludes their
     * collection was fully read. There is nothing to press yet — `enqueueMissingForAi` will
     * not pick a spent row up, so *Try these again* is a route that does not exist — and a
     * count with no button is still the difference between knowing and not.
     */
    private fun runningDetail(ready: State.Ready): String {
        val left = ready.bookmarksLeft
        val head = if (left == 1L) {
            "1 bookmark left to read"
        } else {
            "$left bookmarks left to read"
        }
        val failed = ready.failedEnrichments
        return if (failed == 0L) "$head. Roughly 20 a minute." else "$head · $failed failed"
    }

    /**
     * What a backfill would pick up, before it is pressed.
     *
     * Zero says so in words rather than showing a control that would do nothing.
     */
    private fun idleDetail(waiting: Long): String = if (waiting == 0L) {
        "Every bookmark has already been tried. Nothing is waiting."
    } else {
        "$waiting ${if (waiting == 1L) "bookmark predates" else "bookmarks predate"} AI " +
            "or failed to fetch. Runs in the background, roughly 20 a minute."
    }

    /**
     * Swap the two buttons, in place.
     *
     * Exactly one of them is on screen at a time. `hidden` rather than a rebuild, for the
     * reason given where they are declared: this row repaints every couple of seconds while a
     * backfill runs, and a control replaced under the pointer loses its hover and its focus.
     */
    private fun renderBackfillButtons(ready: State.Ready) {
        val running = ready.isBackfillRunning
        backfillButton.hidden = running
        cancelBackfillButton.hidden = !running

        val waiting = ready.awaitingSummary
        backfillButton.textContent = when {
            ready.isBackfilling -> "Queueing…"
            waiting == 0L -> "Nothing to summarise"
            waiting == 1L -> "Summarise 1 older bookmark"
            else -> "Summarise $waiting older bookmarks"
        }
        backfillButton.disabled = ready.isBackfilling || waiting == 0L

        cancelBackfillButton.textContent = if (ready.isCancelling) "Stopping…" else "Cancel"
        // Disabled only while the request is in flight. Never because of what is in the queue:
        // this button is on screen precisely because there is something in it.
        cancelBackfillButton.disabled = ready.isCancelling
    }

    /**
     * Paint both lists.
     *
     * `setTags` declines to write into a field that has focus, which is what makes this safe
     * to call from a render that runs after every write — including the write the field
     * itself just caused. See [TagListField.setTags].
     */
    private fun renderTagLists(ready: State.Ready) {
        quickTagsField.setKnownTags(ready.knownTags)
        quickTagsField.setTags(ready.settings.saveQuickTags)
        defaultTagsField.setKnownTags(ready.knownTags)
        defaultTagsField.setTags(ready.settings.saveDefaultTags)
    }

    // ── Small builders ───────────────────────────────────────────────────────

    /** A section heading and its one dim explanatory line. */
    private fun heading(title: String, lede: String): HTMLElement {
        val head = element("div", "lp-settings-head")
        head.appendChild(element("h2", "lp-settings-heading", title))
        head.appendChild(element("p", "lp-settings-lede", lede))
        return head
    }

    /** A field: a small mono uppercase label above its control. */
    private fun field(
        label: String,
        control: HTMLElement,
        className: String = "lp-settings-field",
    ): HTMLElement {
        val wrapper = element("div", className)
        wrapper.appendChild(element("div", "lp-settings-mono-label", label.uppercase()))
        wrapper.appendChild(control)
        return wrapper
    }

    /** A card: a title, one control, and a dim caption underneath it. */
    private fun card(title: String, control: HTMLElement, caption: String): HTMLElement {
        val panel = element("section", "lp-panel lp-card")
        panel.appendChild(element("div", "lp-panel-title", title))
        panel.appendChild(control)
        panel.appendChild(element("div", "lp-panel-detail", caption))
        return panel
    }

}

/**
 * *Copy*, and *Copied* for a moment afterwards.
 *
 * A tiny class rather than a function because the button has to be addressable after it is
 * built — it is the thing that changes its own label — and because three of them exist on
 * this window now. Two show the URL and the command on the Connections tab, one shows the
 * raw bookmarklet, and all three should agree about how long *Copied* lasts.
 *
 * `navigator.clipboard` and no fallback, exactly as the edit window's *Copy*: the old
 * hidden-`<textarea>` trick is deprecated everywhere and the modern API is on every browser
 * that can run this bundle. Reached dynamically because Kotlin's `Navigator` external
 * predates it; a failure is swallowed, since it means the page has no clipboard permission
 * and there is nothing useful to say about that from here.
 *
 * @param extraClass a second class so a selector can address one of the three.
 * @param value read at the press rather than captured at construction — the Connections
 *   tab's two are the server's answer, which arrives after this is built and can change
 *   under a reverse proxy that starts forwarding a different host.
 */
private class CopyButton(extraClass: String = "", private val value: () -> String) {
    /** The button, ready to append. */
    val root: HTMLButtonElement = button(
        "Copy",
        if (extraClass.isEmpty()) "lp-settings-secondary" else "lp-settings-secondary $extraClass",
    ) { copy() }

    private fun copy() {
        runCatching { window.navigator.asDynamic().clipboard.writeText(value()) }
        root.textContent = "Copied"
        window.setTimeout({ root.textContent = "Copy" }, COPIED_FOR_MS)
    }

    private companion object {
        /** How long *Copy* says it worked. Long enough to read, short enough not to lie. */
        const val COPIED_FOR_MS = 1400
    }
}

/**
 * One choice out of a fixed set, as a row of the toolkit's own choice buttons.
 *
 * The single control this window makes a choice with — a boolean, a summary length, a
 * language, a count from 0 to 10 are all the same shape, and drawing them the same way is
 * the point. `dt-settings-button-row`, `dt-settings-choice-btn` and `dt-selected` are
 * lunula's class names, worn rather than copied, so this follows the toolkit's chrome and
 * every theme's accent for free and keeps following them after an upgrade.
 *
 * **The rendered state is [select]'s alone.** A press reports an intent and changes nothing
 * on screen; the next state emission is what lights a button. That is what makes a refusal
 * honest — a write the server rejects leaves the row where it was, rather than showing a
 * choice that was not stored — and it is the same discipline Lunicle's `Toggle` keeps.
 *
 * A press on the already-live button is swallowed. `disabled` is not set on it, because a
 * dead-looking button in a row of live ones reads as "not available" rather than "already
 * chosen"; it is simply not an intent, and every view model here treats a call as a change.
 *
 * @param values every option, in the order they are drawn.
 * @param label how one is worded.
 * @param extraRowClass an optional second class on the row — `dt-settings-size-row` for the
 *   tighter pills the toolkit uses on a long ladder of numbers.
 */
private class ChoiceRow<T>(
    private val values: List<T>,
    label: (T) -> String,
    extraRowClass: String = "",
    onChosen: (T) -> Unit,
) {
    /** The row, ready to append. */
    val root: HTMLElement = element(
        "div",
        if (extraRowClass.isEmpty()) "dt-settings-button-row"
        else "dt-settings-button-row $extraRowClass",
    )

    private var chosen: T? = null

    private val buttons: List<HTMLButtonElement> = values.map { value ->
        button(label(value), "dt-settings-choice-btn") {
            if (value != chosen) onChosen(value)
        }.also { root.appendChild(it) }
    }

    /** Light the one that is live. A value not in [values] lights nothing, which is honest. */
    fun select(value: T) {
        chosen = value
        buttons.forEachIndexed { index, element ->
            element.classList.toggle("dt-selected", values[index] == value)
        }
    }

    /**
     * Grey the whole row while a write is in flight.
     *
     * The one row on this window that uses it is agent access, and the reason it is the only
     * one is the reason it exists: everything else here writes the whole settings form, so a
     * second press queues behind the first and lands on the newer value — the mutex in the
     * view model. This row's write is a *different* request against a state the answer
     * replaces wholesale, so a second press mid-flight would be a race between two answers
     * about a security gate, and the honest thing is to make it unpressable for the second it
     * takes.
     */
    fun disable(disabled: Boolean) {
        buttons.forEach { it.disabled = disabled }
    }
}

/**
 * A switch row: a title, an optional dim line under it, and an On/Off pair pushed right.
 *
 * A tiny class rather than a builder function because the row and the control have to be
 * addressable separately — the row is appended once and the pair is written on every state —
 * and returning a pair of elements from a function is the same thing with worse names.
 *
 * ── Two buttons, not a switch ────────────────────────────────────────────────
 *
 * This was a `role="switch"` button drawn as a pill track with a knob that slid, and the
 * note that stood here defended `role="switch"` over a hidden `<input type="checkbox">` on
 * accessibility grounds. That argument was sound and is now moot: the visual it was
 * defending is gone. Lunula spells a boolean as **two buttons with the live one accented**,
 * with no switch anywhere in its own settings sidebar, and a LunaPin window sitting inside
 * that shell should not disagree about what a boolean looks like — see the file header.
 *
 * The accessibility story is at least as good and needs no role at all: two buttons labelled
 * *On* and *Off* announce themselves, where a switch had to carry `aria-checked` in step
 * with a class. The pair is wrapped in a `group` naming the setting, so a screen reader
 * reaching a bare "On" knows what it is on.
 */
private class Toggle(
    private val title: String,
    onToggled: (Boolean) -> Unit,
) {
    private val choice = ChoiceRow(
        values = listOf(true, false),
        label = { if (it) "On" else "Off" },
        onChosen = onToggled,
    )

    /** The whole row, built once. [detail] is the dim line, or null for a bare title. */
    fun row(detail: String?): HTMLElement {
        val row = element("div", "lp-switch-row")
        val text = element("div", "lp-switch-text")
        text.appendChild(element("div", "lp-switch-title", title))
        if (detail != null) text.appendChild(element("div", "lp-panel-detail", detail))
        row.appendChild(text)
        choice.root.setAttribute("role", "group")
        choice.root.setAttribute("aria-label", title)
        row.appendChild(choice.root)
        return row
    }

    /** Paint the position. */
    fun set(value: Boolean) = choice.select(value)

    /** Grey both buttons while a write is in flight. See [ChoiceRow.disable]. */
    fun disable(disabled: Boolean) = choice.disable(disabled)
}

/** How a summary length is offered, in the order `SummaryLengths.all` lists them. */
private fun lengthLabel(length: String): String = when (length) {
    SummaryLengths.ONE_LINE -> "One line"
    SummaryLengths.DETAILED -> "Detailed"
    else -> "Short"
}

/**
 * How a vocabulary target is offered, in the order `TagVocabularyTargets.all` lists them.
 *
 * The numbers are drawn bare — *50*, not *50 tags* — because they sit in one row under a
 * title that already says what is being counted, and a unit repeated five times is four
 * more than the row needs. *No limit* carries words because it is the one value that is not
 * a number.
 */
private fun vocabularyLabel(target: Int): String =
    if (target == TagVocabularyTargets.NO_LIMIT) "No limit" else target.toString()

/** How a language is offered. See `SummaryLanguages`. */
private fun languageLabel(language: String): String = when (language) {
    SummaryLanguages.ENGLISH -> "English"
    SummaryLanguages.SWEDISH -> "Swedish"
    else -> "Match the page"
}
